import { useCallback, useMemo } from "react";
import { useHoverIntent } from "./useHoverIntent.ts";
import { useRoutePreload } from "./useRoutePreload.ts";

export interface LinkIntent {
    start: () => void;
    end: () => void;
    focus: () => void;
}

export interface UseLinkIntentOptions {
    prefetch?: () => void;
}

export function useLinkIntent(href: string | undefined, options: UseLinkIntentOptions = {}): LinkIntent {
    const { prefetch } = options;

    const preloadRoute = useRoutePreload();

    const handleIntent = useCallback(
        (intentHref: string) => {
            preloadRoute(intentHref);
            prefetch?.();
        },
        [preloadRoute, prefetch]
    );

    const hoverIntent = useHoverIntent(handleIntent);

    const start = useCallback(() => {
        if (href) {
            hoverIntent.start(href);
        }
    }, [hoverIntent, href]);

    const end = useCallback(() => {
        if (href) {
            hoverIntent.end(href);
        }
    }, [hoverIntent, href]);

    const focus = useCallback(() => {
        if (href) {
            handleIntent(href);
        }
    }, [handleIntent, href]);

    return useMemo(() => ({ start, end, focus }), [start, end, focus]);
}
